import { Collapse } from "./Collapse";
import { MarkdownMemo } from "./MarkdownMemo";
import { SectionConfig } from "../sectionConfig";
import { memoData } from "../memoData";
import { chartData } from "@/features/Charts/data/chartData";

import { AssetProgression } from "@/features/Charts/AssetProgression";
import { BalanceOverTime } from "@/features/Charts/BalanceOverTime";
import { CashFlowProgression } from "@/features/Charts/CashflowProgression";
import { DebtComposition } from "@/features/Charts/DebtComposition";
import { DebtServiceCoverage } from "@/features/Charts/DebtServiceCoverage";
import { DebtToAssets } from "@/features/Charts/DebtToAssets";
import { DebtToCashflows } from "@/features/Charts/DebtToCashflows";
import { Liquidity } from "@/features/Charts/Liquidity";
import { DisbursementsAndRepayments } from "@/features/Charts/DisbursementsAndRepayments";

interface SectionContentProps {
    sectionConfig: SectionConfig;
    tabKey: string;
    isCondensed?: boolean;
}

const chartComponents = {
    AssetProgression,
    BalanceOverTime,
    CashFlowProgression,
    DebtComposition,
    DebtServiceCoverage,
    DebtToAssets,
    DebtToCashflows,
    Liquidity,
    DisbursementsAndRepayments,
};

export function SectionContent({
    sectionConfig,
    tabKey,
    isCondensed = false,
}: SectionContentProps) {
    const widgets = sectionConfig.widgets.filter((widget) => {
        if (widget.isChart) {
            return chartData[widget.dataKey] && widget.chartType;
        }
        return memoData[sectionConfig.id]?.[widget.dataKey];
    });

    const renderWidget = (widget) => {
        if (widget.isChart) {
            const ChartComponent = chartComponents[widget.chartType];

            if (!ChartComponent) {
                console.warn(`Chart type "${widget.chartType}" not found`);
                return null;
            }

            return (
                <ChartComponent
                    key={widget.id + tabKey}
                    data={chartData[widget.dataKey]}
                    isCondensed={isCondensed}
                />
            );
        } else {
            return (
                <Collapse
                    key={widget.id + tabKey}
                    title={widget.title}
                    defaultOpen={widget.defaultOpen}
                >
                    <MarkdownMemo
                        content={memoData[sectionConfig.id][widget.dataKey]}
                    />
                </Collapse>
            );
        }
    };

    const midPoint = Math.ceil(widgets.length / 2);
    const modifier = sectionConfig.id === "introduction" ? 2 : 0;
    const leftColumnWidgets = widgets.slice(0, midPoint + modifier);
    const rightColumnWidgets = widgets.slice(midPoint + modifier);

    return (
        <>
            <div className="flex flex-col gap-2">
                {leftColumnWidgets.map(renderWidget)}
            </div>
            <div className="flex flex-col gap-2">
                {rightColumnWidgets.map(renderWidget)}
            </div>
        </>
    );
}
